import { Component, Output, EventEmitter, OnInit, Input, OnDestroy } from '@angular/core';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { NestedTreeControl } from '@angular/cdk/tree';
import { Subject } from 'rxjs';
import { isNil } from 'lodash';
import { EfficiencyAnalysisService } from '@app/shared/services/efficiency-analysis.service';
import { NotificationDialogComponent } from '@app/shared/dialog/notification-dialog/notification-dialog.component';
import { cssVariables } from '@env/css-vars';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { BuildingEfficiencyWizardStore } from '@app/efficiency-analysis/components/building-efficiency-wizard/building-efficiency-wizard.component.store';
import { GetAddressPipe } from '@app/shared/pipes/get-address.pipe';
import { EABuilding } from '@app/efficiency-analysis/models/buildings/efficiency-analysis-buildings';
import { takeUntil } from 'rxjs/operators';

export interface BuildingNode {
  buildingTypeName: string;
  guid: string;
  buildingName: string;
  buildingTypeId: string;
  address: string;
  efficiencyAnalysisDate: string;
  efficiencyPercentageElectricity: string;
  city: number;
  street: string;
  efficiencyPercentageHeat: number;
  electricityColorStatus: string;
  electricityComputedValue: number;
  heatColorStatus: string;
  heatComputedValue: number;
  buildingType: string;
  children?: BuildingNode[];
}

@Component({
  selector: 'app-add-object-tree',
  templateUrl: './add-object-tree.component.html',
  styleUrls: ['./add-object-tree.component.scss'],
  providers: [GetAddressPipe],
})
export class AddObjectTreeComponent implements OnInit, OnDestroy {
  protected unsubscribe$: Subject<void> = new Subject<void>();
  @Input() showTree: boolean = false;
  @Input() buildingsWithoutEA: EABuilding[] = [];
  @Output() isProcessDone = new EventEmitter<{ isProcessDone: boolean; building: any }>();
  public dataSource = new MatTreeNestedDataSource<BuildingNode>();
  public selectedBldg: any;
  public selectedBuilding: EABuilding = {} as EABuilding;
  public showQuestionnaire = false;
  public treeControl = new NestedTreeControl<BuildingNode>((node) => node.children);

  private selectedBuilding$: Subject<EABuilding> = new Subject<EABuilding>();

  constructor(
    public matDialog: MatDialog,
    public translate: TranslateService,
    private buildingEfficiencyAnalysisWizardStore: BuildingEfficiencyWizardStore,
    private efficiencyAnalysisService: EfficiencyAnalysisService,
  ) {
    this.dataSource.data = this.efficiencyAnalysisService.buildingsWithoutEA as any[];
  }

  public ngOnInit(): void {
    this.selectedBuilding$.pipe(takeUntil(this.unsubscribe$)).subscribe((value: EABuilding) => (this.selectedBuilding = value));
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public hasChild(_: number, node: BuildingNode): boolean {
    return !!node.children && node.children.length > 0;
  }

  private getBuildingDetails(guid: string): EABuilding {
    let building: EABuilding = {} as EABuilding;
    if (!isNil(guid)) {
      building = this.buildingsWithoutEA.find((el: EABuilding) => el.guid === guid) as EABuilding;
    }
    return building;
  }

  public openQuestionnaire(guid: string): void {
    this.selectedBuilding$.next(this.getBuildingDetails(guid));
    this.buildingEfficiencyAnalysisWizardStore.setBuilding(this.getBuildingDetails(guid));
    this.showQuestionnaire = true;
  }

  private closeChart(): void {
    this.showQuestionnaire = false;
    this.showTree = false;
  }

  public closeQuestionnaire(wordingVisibility: boolean): void {
    if (wordingVisibility) {
      this.showQuestionnaire = false;
    } else {
      this.showQuestionnaire = false;
      if (this.efficiencyAnalysisService.oldBuildingData.length > 0) {
        this.revertSelectedBuildingWizardData();
      }
    }
  }

  public getButtonStatus(): boolean {
    return isNil(this.selectedBldg);
  }

  private revertSelectedBuildingWizardData(): void {
    const oldData = this.efficiencyAnalysisService.oldBuildingData[0];
    const building: EABuilding =
      this.buildingsWithoutEA[this.buildingsWithoutEA.findIndex((el: EABuilding) => el.guid === this.efficiencyAnalysisService.oldBuildingData[0].guid)];

    building.floorArea = oldData.floorArea;

    building.electricityConsumption = oldData.electricityConsumption;
    building.heatingOilConsumption = oldData.heatingOilConsumption;
    building.naturalGasConsumption = oldData.naturalGasConsumption;
    building.lpgConsumption = oldData.lpgConsumption;
    building.districtHeatingConsumption = oldData.districtHeatingConsumption;
    building.electricityHeatConsumption = oldData.electricityHeatConsumption;
    building.pelletWoodConsumption = oldData.pelletWoodConsumption;

    building.electricityFromDate = oldData.electricityFromDate;
    building.heatingOilFromDate = oldData.heatingOilFromDate;
    building.naturalGasFromDate = oldData.naturalGasFromDate;
    building.lpgFromDate = oldData.lpgFromDate;
    building.districtHeatingFromDate = oldData.districtHeatingFromDate;
    building.electricityHeatFromDate = oldData.electricityHeatFromDate;
    building.pelletWoodFromDate = oldData.pelletWoodFromDate;

    building.electricityToDate = oldData.electricityToDate;
    building.heatingOilToDate = oldData.heatingOilToDate;
    building.naturalGasToDate = oldData.naturalGasToDate;
    building.lpgToDate = oldData.lpgToDate;
    building.districtHeatingToDate = oldData.districtHeatingToDate;
    building.electricityHeatToDate = oldData.electricityHeatToDate;
    building.pelletWoodToDate = oldData.pelletWoodToDate;

    building.electricityComputedValue = oldData.electricityComputedValue;
    building.heatComputedValue = oldData.heatComputedValue;
    building.electricityColorStatus = oldData.electricityColorStatus;
    building.heatColorStatus = oldData.heatColorStatus;
    building.efficiencyAnalysisDate = oldData.efficiencyAnalysisDate;
    building.efficiencyPercentageElectricity = oldData.efficiencyPercentageElectricity;
    building.efficiencyPercentageHeat = oldData.efficiencyPercentageHeat;
    building.notes = oldData.notes;

    this.buildingsWithoutEA[this.buildingsWithoutEA.findIndex((el: EABuilding) => el.guid === this.efficiencyAnalysisService.oldBuildingData[0].guid)] =
      building;
    this.selectedBuilding$.next(this.getBuildingDetails(building.guid));
  }

  public saveEfficiencyAnalysis(): void {
    this.efficiencyAnalysisService.isRecalculated = true;
    this.isProcessDone.emit({ isProcessDone: true, building: this.selectedBuilding });

    const matRef = this.matDialog.open(NotificationDialogComponent, {
      data: {
        descriptionText: this.translate.instant('module.efficiencyAnalysis.saveSuccessText'),
        descriptionFaClass: 'fa-check-circle fa-2x',
        descriptionFaColor: cssVariables['--confirm-success-color'],
        hideClose: true,
      },
      panelClass: ['-md'],
    });

    matRef.afterClosed().subscribe(() => {
      this.efficiencyAnalysisService.isRecalculated = false;
      this.closeChart();
    });
  }
}
